import * as React from "react";
import { useId, FloatingOverlay, FloatingPortal, FloatingFocusManager, useFloating, offset, shift, size, autoUpdate, useHover, safePolygon, useClick, useDismiss, useRole, useInteractions, useTransitionStatus } from "@floating-ui/react";
import clsx from "clsx";
import { useMediaQuery } from "react-responsive";
import { CloseButton } from "../../close-button/close-button.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
import { VisibilityProvider } from "../../helpers/visibility-provider/visibility-provider.mjs";
import { HeaderInnerContext } from "../header/header.mjs";
const HeaderDropdown = ({
  reference,
  container: portalContainer,
  asModal,
  isShown,
  onClose,
  onToggle,
  closeButtonLabel,
  header,
  children,
  footer,
  className,
  size: size2 = "large",
  padding: propPadding,
  showCloseButton = true,
  offsetY = 5,
  "aria-labelledby": ariaLabelledBy,
  id,
  contentRef
}) => {
  var _a, _b;
  const [referenceId, setReferenceId] = React.useState(null);
  const internalReferenceId = useId();
  const state = useHeaderDropdown({ isShown, onClose, onToggle, asModal, offsetY });
  const { setOpen } = state;
  const handleCloseClick = React.useCallback(
    (event) => {
      event.preventDefault();
      setOpen(false);
    },
    [setOpen]
  );
  const labelledBy = (_a = ariaLabelledBy != null ? ariaLabelledBy : referenceId) != null ? _a : internalReferenceId;
  React.useEffect(() => {
    if (React.isValidElement(reference)) {
      setReferenceId(reference.props.id);
    }
  }, [reference]);
  const content = /* @__PURE__ */ React.createElement(VisibilityProvider, { isVisible: state.isMounted }, /* @__PURE__ */ React.createElement(FloatingOverlay, { lockScroll: true, className: "header__dropdown-overlay" }, /* @__PURE__ */ React.createElement(
    "div",
    {
      id,
      ref: state.refs.setFloating,
      style: state.floatingStyles,
      className: clsx(
        "header__dropdown",
        "header__dropdown--padding-".concat(propPadding != null ? propPadding : "none"),
        "header__dropdown--".concat(size2),
        "is-".concat(state.status),
        className
      ),
      ...state.getFloatingProps({
        tabIndex: -1,
        role: asModal ? "dialog" : void 0,
        "aria-labelledby": asModal ? labelledBy : void 0,
        // Prevent click events from propagating to portal parents
        onClick: (e) => e.stopPropagation()
      })
    },
    /* @__PURE__ */ React.createElement("div", { className: "header__dropdown-inner", ref: contentRef }, showCloseButton && /* @__PURE__ */ React.createElement(
      CloseButton,
      {
        className: "header__dropdown-close",
        onClick: handleCloseClick,
        attributes: { id: "header-dropdown-button" },
        label: closeButtonLabel
      }
    ), header && /* @__PURE__ */ React.createElement("div", { className: "header__dropdown-header" }, header), /* @__PURE__ */ React.createElement("div", { className: "header__dropdown-content" }, children), footer && /* @__PURE__ */ React.createElement("div", { className: "header__dropdown-footer" }, footer))
  )));
  return /* @__PURE__ */ React.createElement(React.Fragment, null, React.isValidElement(reference) ? React.cloneElement(
    reference,
    state.getReferenceProps({
      ...reference.props,
      ref: (el) => {
        var _a2, _b2;
        state.refs.setReference(el);
        (_b2 = (_a2 = reference.props).ref) == null ? void 0 : _b2.call(_a2, el);
      },
      id: (_b = reference.props.id) != null ? _b : internalReferenceId,
      role: "button",
      onClick: (event) => {
        var _a2, _b2;
        event.preventDefault();
        (_b2 = (_a2 = reference.props).onClick) == null ? void 0 : _b2.call(_a2, event);
      }
    })
  ) : null, state.isMounted && /* @__PURE__ */ React.createElement(FloatingPortal, { root: portalContainer }, /* @__PURE__ */ React.createElement(
    FloatingFocusManager,
    {
      context: state.context,
      modal: !!asModal,
      order: asModal ? ["content"] : ["reference", "content"],
      initialFocus: state.reason === "hover" ? -1 : 0
    },
    content
  )));
};
function useHeaderDropdown({ isShown, onClose, onToggle, asModal = false, offsetY }) {
  var _a;
  const { containerRef } = React.useContext(HeaderInnerContext);
  const [reason, setReason] = React.useState(void 0);
  const { media } = useTheme();
  const isSmallerThanHeaderBreak = useMediaQuery({ maxWidth: media.width.md.max });
  const handleOpenChange = React.useCallback(
    (nextOpen, _e, changeReason) => {
      const normalizedChangeReason = changeReason === "safe-polygon" ? "hover" : changeReason;
      setReason(normalizedChangeReason);
      if (onToggle && nextOpen !== isShown) {
        onToggle(nextOpen);
      }
      if (onClose && nextOpen === false) {
        onClose();
      }
    },
    [onClose, isShown, onToggle]
  );
  const modalShiftPadding = isSmallerThanHeaderBreak ? 0 : 32;
  const data = useFloating({
    placement: "bottom",
    open: isShown,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetY),
      shift({ padding: asModal ? modalShiftPadding : 0, boundary: asModal ? (_a = containerRef.current) != null ? _a : void 0 : void 0 }),
      size({
        padding: isSmallerThanHeaderBreak ? 0 : 16,
        rootBoundary: "document",
        apply({ availableHeight, elements }) {
          const prop = isSmallerThanHeaderBreak ? "height" : "maxHeight";
          const otherProp = isSmallerThanHeaderBreak ? "maxHeight" : "height";
          Object.assign(elements.floating.style, {
            [prop]: "".concat(Math.max(100, availableHeight), "px"),
            [otherProp]: ""
            // reset other prop when  e.g. resizing, so it wouldn't get stuck
          });
        }
      })
    ]
  });
  const { context } = data;
  const hover = useHover(context, {
    handleClose: safePolygon(),
    enabled: asModal === false,
    delay: {
      open: 100,
      close: 300
    }
  });
  const click = useClick(context, {
    enabled: true
  });
  const dismiss = useDismiss(context, {
    outsidePress: true
  });
  const role = useRole(context, {
    enabled: asModal,
    role: "dialog"
  });
  const interactions = useInteractions([hover, click, dismiss, role]);
  const transitionStatus = useTransitionStatus(context, { duration: 100 });
  return React.useMemo(
    () => ({
      open: isShown,
      setOpen: handleOpenChange,
      reason,
      ...interactions,
      ...data,
      ...transitionStatus
    }),
    [isShown, handleOpenChange, interactions, data, transitionStatus, reason]
  );
}
export {
  HeaderDropdown
};
